<template>
  <v-data-table-server
    v-model:options="options"
    dense
    height="300"
    :headers="headers"
    :loading="loading"
    :items="items"
    :items-length="totalItems"
    class="elevation-1"
    :items-per-page-options="footerProps.itemsPerPageOptions"
    @update:options="updateTable"
  >
    <template #top>
      <v-card-title>
        <v-toolbar flat>
          <v-toolbar-title>Tickets</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="..."
            dense
            single-line
            hide-details
            class="mr-2"
            @keyup.enter="handleSearch"
          />
          <v-select
            v-model="selectedLookupType"
            :items="lookupTypes"
            label="Lookup By"
            dense
            hide-details
            single-line
            style="max-width: 250px;"
            class="mr-2"
          />
          <v-btn
            color="primary"
            dense
            @click="handleSearch"
          >
            Search
          </v-btn>
          <v-spacer />
        </v-toolbar>
      </v-card-title>
    </template>
    <template #item.actions="{ item }">
      <disabled-for-lack-of-role-btn
        v-if="!isTicketStatusViewable(item)"
        text="View"
        permission="view ticket::show"
        icon="mdi-ticket"
        :x-small="true"
        button-class="mr-2"
        :custom-message="statusNotOpenMessage"
      />
      <v-btn
        v-else-if="hasPermission('view ticket::show') && item.has_access"
        x-small
        color="primary"
        class="mr-2"
        @click="verifyTicketOwnerStatus(item)"
      >
        <v-icon
          left
          dark
        >
          mdi-ticket
        </v-icon>
        View
      </v-btn>
      <disabled-for-lack-of-role-btn
        v-else-if="hasPermission('view ticket::show') && !item.has_access"
        text="View"
        permission="view ticket::show"
        icon="mdi-ticket"
        :x-small="true"
        button-class="mr-2"
        custom-message="You do not have access to this Ticket. It is assigned to someone else."
      />
      <disabled-for-lack-of-role-btn
        v-else
        text="View"
        permission="view ticket::show"
        icon="mdi-ticket"
        :x-small="true"
        button-class="mr-2"
      />
    </template>
  </v-data-table-server>
</template>

<script>
import { mapActions } from 'vuex';
import dataTableMixin from '@/mixins/dataTableMixin.js';
import DisabledForLackOfRoleBtn from '../DisabledForLackOfRoleBtn.vue';

export default {
  name: 'TicketsIndex',

  components: {
    DisabledForLackOfRoleBtn,
  },

  mixins: [
    dataTableMixin,
  ],

  data() {
    return {
      headers: [
        { title: 'Ticket ID', key: 'id', sortable: false },
        { title: 'Customer ID', key: 'customer_id', sortable: false },
        { title: 'Email', key: 'email', sortable: false },
        { title: 'Status', key: 'status', sortable: false },
        { title: 'Actions', key: 'actions', sortable: false },
      ],
      search: '',
      statusNotOpenMessage:
        'This ticket is in a closed or resolved state and cannot be accessed from this list.',
      lookupTypes: [],
      selectedLookupType: 'Email',
      updateTableTimeout: null,
    };
  },

  mounted() {
    this.fetchLookupTypes();
  },

  methods: {
    /**
     * Lookup returns work-queue tickets only. HubSpot workflows (and similar) may surface status as a
     * single label or compound text (commas, spaces, pipes, etc.). Any token matching an open-queue
     * word allows View; any terminal token blocks; unknown tokens default to allow.
     */
    isTicketStatusViewable(item) {
      const ticketId = item?.ticket_id ?? item?.ticketId;
      if (ticketId === undefined || ticketId === null || ticketId === '') {
        return false;
      }
      const raw = item?.status;
      if (raw === undefined || raw === null || String(raw).trim() === '') {
        return true;
      }
      const normalized = String(raw).toLowerCase().trim();
      const openLike = new Set(['open', 'new', 'pending', 'unassigned', 'assigned', 'hold']);
      const terminal = new Set(['closed', 'solved', 'done', 'completed', 'archived', 'resolved']);
      const segments = normalized
        .split(/[,;|/\s-]+/)
        .map((t) => t.trim())
        .filter(Boolean);
      const tokens = segments.length > 0 ? segments : [normalized];
      if (tokens.some((t) => openLike.has(t))) {
        return true;
      }
      if (tokens.some((t) => terminal.has(t))) {
        return false;
      }
      return true;
    },
    handleSearch() {
      // Validate that both lookup type is specified AND input is not empty
      if (this.selectedLookupType && this.search.trim() !== '') {
        this.updateTable();
      }
    },
    fetchLookupTypes() {
      this.$axios
        .get('api/v1/support/ticket/lookup-types')
        .then((res) => {
          this.lookupTypes = res.data;
        })
        .catch((error) => {
          console.error('Error fetching lookup types:', error);
        });
    },
    updateTable() {
      // Clear any pending timeout to prevent duplicate calls
      if (this.updateTableTimeout) {
        clearTimeout(this.updateTableTimeout);
      }
      this.updateTableTimeout = setTimeout(() => this.sendUpdateTable(), 750);
    },
    sendUpdateTable() {
      // Never attempt lookup unless both input is not empty and lookup type is selected
      if (this.search.trim() === '' || !this.selectedLookupType) return null;

      this.loading = true;

      return this.$axios
        .post('api/v1/support/ticket/lookup', {
          value: this.search,
          type: this.selectedLookupType,
        })
        .then((res) => {
          this.loading = false;
          this.items = res.data.data;
          this.totalItems = res.data.total;
        })
        .catch((error) => {
          this.loading = false;
          this.items = [];
          let errMsg = 'There was an error processing your request';
          if (error.response && error.response.status === 404 && error.response.data) {
            errMsg = error.response.data.message;
          }

          if (error.response && error.response.status === 422) {
            // eslint-disable-next-line no-console
            console.log(error.response);

            errMsg = error.response.data.message;
            if (error.response.data.errors) {
              Object.values(error.response.data.errors).forEach((val) => {
                errMsg += `\n ${val}`;
              });
            }
          }

          if (error.response && error.response.status === 500) {
            errMsg = error.response.data.message;
          }

          this.loadingModel = false;
          this.setNotification({
            message: errMsg,
            color: 'red',
          });
        });
    },
    verifyTicketOwnerStatus(item) {
      this.loading = true;
      this.$axios
        .post('api/v1/support/ticket/verify-owner-status', {
          ticketId: item.ticket_id,
        })
        .then((res) => {
          this.loading = false;
          if (res.data.data && res.data.data.verified === true) {
            this.resetTicket();
            this.$router.push({
              name: 'ticket-show',
              params: {
                id: item.ticket_id,
              },
            });
          }
        })
        .catch((error) => {
          this.loading = false;
          let errMsg = 'There was an error processing your request';
          if (error.response && error.response.status === 404 && error.response.data) {
            errMsg = error.response.data.message;
          }
          this.loadingModel = false;
          this.setNotification({
            message: errMsg,
            color: 'red',
          });
        });
    },
    ...mapActions('notification', ['setNotification']),
    ...mapActions('ticket', ['resetTicket']),
  },

};
</script>

<style>
  .v-table__wrapper {
    height: auto !important;
  }
</style>
